import React, { useCallback, useEffect, useState } from "react";
import { useGetUpcomingEventsMutation } from "../../api/api";
import {
  MdOutlineKeyboardArrowDown,
  MdLaptopMac,
  MdSearchOff,
  MdOutlineUpcoming,
} from "react-icons/md";
import { IoCalendarOutline, IoHomeOutline } from "react-icons/io5";
import { IoMdTime } from "react-icons/io";
import { FaRegUser, FaPerson } from "react-icons/fa6";
import { CgDetailsMore } from "react-icons/cg";
import { TbLocation, TbReload } from "react-icons/tb";
import { BsCameraVideo } from "react-icons/bs";
import { AiOutlineFileDone } from "react-icons/ai";

import Reschedule from "./RescheduleBooking";
import { formatDateTime, useDecodedTokenResult } from "../../lib/utils";
import { useNavigate } from "react-router-dom";
import { Tabs, TabsList, TabsTrigger } from "../../@/components/ui/tabs";
import moment from "moment";
import "moment-timezone";
import { ModeLookup } from "../../lib/constants";
import { RiUserLine } from "react-icons/ri";

interface UpcomingEvents {
  id: number;
  customerId: number;
  vendorId: number;
  productId: number;
  bookingDate: string;
  startTime: string;
  endTime: string;
  calenderLink: string;
  googleMeetLink: string;
  customerName: string;
  calenderEventId: string;
  orderId: number;
  latitude: number;
  longitude: number;
  productModeId: number | null;
  productName: string;
}

const Booking = () => {
  // const [productId, setProductId] = useState<number | null>(null);

  const [getUpcomingEvents, { isLoading: isUpcomingEventsLoading }] =
    useGetUpcomingEventsMutation();

  const [data, setData] = useState<UpcomingEvents[]>([]);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [pageSize] = useState<number>(10);
  const [isNextPage, setIsNextPage] = useState(false);
  const [activeTab, setActiveTab] = useState<"upcoming" | "Past">("upcoming");
  const [dropdownOpen, setDropdownOpen] = useState<number | null>(null);
  const [selectedEvent, setSelectedEvent] = useState<UpcomingEvents | null>(
    null
  );

  const { vendorId } = useDecodedTokenResult();
  const navigate = useNavigate();
  const currentDate = formatDateTime(new Date(), "YYYY-MM-DDTHH:mm:ssZ");

  const fetchUpcomingEvents = useCallback(async () => {
    if (isUpcomingEventsLoading) return;
    try {
      const response: any = await getUpcomingEvents({
        getUpcomingEventsDto: {
          pageSize: pageSize,
          pageNumber: pageNumber,
          vendorId: vendorId,
          isPastEvents: activeTab === "Past" ? true : false,
          getTotalCount: true,
          currentDate: currentDate,
        },
      }).unwrap();

      if (pageNumber < response.data.totalPages) {
        setIsNextPage(true);
      } else {
        setIsNextPage(false);
      }

      if (pageNumber > 1) {
        setData((prevData) => [
          ...prevData,
          ...(response.data.upcomingEvents || []),
        ]);
      } else {
        setData(response.data.upcomingEvents || []);
      }
    } catch (error) {
      console.log("Error fetching events:", error);
    }
  }, [getUpcomingEvents, activeTab, pageNumber]);

  const handleLoadMore = () => {
    if (!isNextPage) return;
    setPageNumber((prev) => prev + 1);
  };

  useEffect(() => {
    fetchUpcomingEvents();
  }, [fetchUpcomingEvents]);

  useEffect(() => {
    const handleClickOutside = () => setDropdownOpen(null);
    document.addEventListener("click", handleClickOutside);
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, []);

  const toggleDropdown = (id: number) => {
    setDropdownOpen(dropdownOpen === id ? null : id);
  };

  const handleOpenEditDialog = (event: UpcomingEvents) => {
    setSelectedEvent(event);
  };

  const handleCloseEditDialog = () => {
    setSelectedEvent(null);
  };

  const handleDetailEvent = (eventId: number) => {
    navigate(`/edit-order/${eventId}`);
  };

  const groupedEvents = data.reduce((acc, event) => {
    const date = formatDateTime(event.bookingDate, "ddd, MMM D");
    if (!acc[date]) acc[date] = [];
    acc[date].push(event);
    return acc;
  }, {} as Record<string, UpcomingEvents[]>);

  return (
    <div className="p-4">
      <div className="flex space-x-4 mb-4 items-center border-b relative">
        <div
          className={`absolute bottom-0 h-0.5 bg-customGreenColor transition-all duration-300 ease-in-out`}
          style={{
            width: activeTab === "upcoming" ? "116px" : "78px",
            transform: `translateX(${
              activeTab === "upcoming" ? "25px" : "178px"
            })`,
          }}
        />

        <button
          onClick={() => setActiveTab("upcoming")}
          className={`flex flex-row gap-2 text-base px-4 py-2 relative transition-colors duration-300 ${
            activeTab === "upcoming"
              ? "font-bold text-customGreenColor"
              : "text-gray-500 hover:text-gray-700"
          }`}
        >
          <MdOutlineUpcoming size={20} />
          Upcoming
        </button>

        <button
          onClick={() => setActiveTab("Past")}
          className={`flex flex-row gap-2 text-base px-4 py-2 relative transition-colors duration-300 ${
            activeTab === "Past"
              ? "font-bold text-customGreenColor"
              : "text-gray-500 hover:text-gray-700"
          }`}
        >
          <AiOutlineFileDone size={20} />
          Older
        </button>
      </div>

      <div className="min-h-[75vh] transition-opacity duration-300">
        {isUpcomingEventsLoading ? (
          <div>
            {[...Array(2)].map((_, groupIndex) => (
              <div key={groupIndex} className="mb-8">
                {/* Date header skeleton */}
                <div className="animate-pulse mb-2 flex items-center gap-2">
                  <div className="h-5 w-5 bg-gray-300 rounded"></div>
                  <div className="h-6 bg-gray-300 rounded w-32"></div>
                </div>

                {/* Events grid skeleton */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {[...Array(3)].map((_, eventIndex) => (
                    <div
                      key={eventIndex}
                      className="bg-white rounded-lg shadow-md border border-gray-200 w-full mb-4"
                    >
                      {/* Header Section Skeleton */}
                      <div className="p-4 border-b border-gray-200">
                        <div className="flex items-start gap-3 mb-2">
                          <div className="h-4 w-4 bg-gray-300 rounded mt-1"></div>
                          <div className="flex-1">
                            <div className="flex items-center justify-between">
                              <div className="h-5 bg-gray-300 rounded w-1/3"></div>
                              <div className="h-4 bg-gray-300 rounded-full w-24"></div>
                            </div>
                          </div>
                        </div>

                        {/* Time slot skeleton */}
                        <div className="flex items-center gap-2 mt-2">
                          <div className="h-4 w-4 bg-gray-300 rounded"></div>
                          <div className="h-4 bg-gray-300 rounded w-40"></div>
                        </div>

                        {/* Customer name skeleton */}
                        <div className="flex items-center gap-2 mt-2">
                          <div className="h-4 w-4 bg-gray-300 rounded"></div>
                          <div className="h-6 bg-gray-300 rounded w-1/2"></div>
                        </div>
                      </div>

                      {/* Footer Section Skeleton */}
                      <div className="p-4 flex justify-between items-center">
                        <div className="flex items-center gap-1">
                          <div className="h-4 w-4 bg-gray-300 rounded"></div>
                          <div className="h-4 bg-gray-300 rounded w-24"></div>
                        </div>
                        <div className="h-9 bg-gray-300 rounded w-28"></div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : Object.keys(groupedEvents).length > 0 ? (
          <div>
            {Object.entries(groupedEvents).map(([date, events]) => (
              <div key={date}>
                <h2 className="text-xl font-semibold text-gray-600 mb-2 flex items-center gap-2">
                  <IoCalendarOutline
                    size={20}
                    className="text-gray-600 text-xl"
                  />
                  {date}
                </h2>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {events.map((event, index) => (
                    <div
                      key={event.startTime + new Date().getTime() + index}
                      className="bg-white rounded-lg shadow-md border border-gray-200 w-full mb-4"
                    >
                      <div className="p-4 border-b border-gray-200">
                        <div className="flex items-start gap-3 mb-2">
                          {event.productModeId ===
                          ModeLookup.WALK_IN_SERVICE ? (
                            <FaPerson
                              size={16}
                              className="text-gray-600 mt-1"
                            />
                          ) : event.productModeId ===
                            ModeLookup.HOME_SERVICE ? (
                            <IoHomeOutline
                              size={16}
                              className="text-gray-600 mt-1"
                            />
                          ) : (
                            <MdLaptopMac
                              size={16}
                              className="text-gray-600 mt-1"
                            />
                          )}
                          <div className="flex-1">
                            <div className="flex items-center justify-between">
                              <h3 className="text-lg font-semibold text-gray-900 leading-tight break-all">
                                {event.productName}
                              </h3>
                              <span className="text-xs font-medium text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full ml-2">
                                {event.productModeId ===
                                ModeLookup.WALK_IN_SERVICE
                                  ? "In-Person Service"
                                  : event.productModeId ===
                                    ModeLookup.HOME_SERVICE
                                  ? "Home Service"
                                  : "Online Service"}
                              </span>
                            </div>
                          </div>
                        </div>

                        <p className="text-gray-500 text-sm flex items-center gap-2">
                          <IoMdTime
                            size={16}
                            className="text-gray-500 text-sm"
                          />
                          {formatDateTime(event.startTime, "hh:mm A")} -{" "}
                          {formatDateTime(event.endTime, "hh:mm A")} (
                          {moment(event.startTime)
                            .tz(moment.tz.guess())
                            .format("z")}
                          )
                        </p>
                        <p className="text-base font-semibold text-gray-800 mt-1 flex items-center gap-2">
                          <RiUserLine size={16} className="text-gray-600" />
                          {event.customerName}
                        </p>
                      </div>

                      <div className="p-4 flex justify-between items-center">
                        <div className="relative">
                          <button
                            className="text-gray-500 hover:text-gray-800 flex items-center gap-1 text-sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleDropdown(event.id);
                            }}
                          >
                            <CgDetailsMore className="text-gray-500 text-sm" />
                            More Actions{" "}
                            <MdOutlineKeyboardArrowDown className="text-sm" />
                          </button>
                          {dropdownOpen === event.id && (
                            <div className="absolute left-0 mt-2 w-36 bg-white border border-gray-200 rounded-md shadow-lg">
                              {activeTab === "upcoming" && (
                                <button
                                  className="block w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                                  onClick={() => handleOpenEditDialog(event)}
                                >
                                  <IoMdTime className="text-gray-500 text-sm" />
                                  Reschedule
                                </button>
                              )}
                              <button
                                className="block w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                                onClick={() => handleDetailEvent(event.orderId)}
                              >
                                <CgDetailsMore className="text-gray-500 text-sm" />
                                Detail
                              </button>
                            </div>
                          )}
                        </div>

                        {activeTab === "upcoming" &&
                          event.productModeId !==
                            ModeLookup.WALK_IN_SERVICE && (
                            <a
                              href={
                                event.latitude && event.longitude
                                  ? `https://www.google.com/maps/place/${event.latitude},${event.longitude}`
                                  : event.googleMeetLink
                                  ? event.googleMeetLink
                                  : event.calenderLink
                              }
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-2 justify-center bg-customGreenColor text-white text-sm px-4 py-2 rounded-lg shadow hover:bg-green-800 transition"
                            >
                              {event.latitude && event.longitude ? (
                                <>
                                  <TbLocation className="text-white text-sm" />
                                  View in Map
                                </>
                              ) : event.googleMeetLink ? (
                                <>
                                  <BsCameraVideo
                                    size={16}
                                    className="text-white text-sm"
                                  />
                                  Join Call
                                </>
                              ) : (
                                <>
                                  <IoCalendarOutline className="text-white text-sm" />
                                  View in Calendar
                                </>
                              )}
                            </a>
                          )}

                        {activeTab === "upcoming" &&
                          event.productModeId ===
                            ModeLookup.WALK_IN_SERVICE && (
                            <span className="bg-green-50 text-customGreenColor px-3 py-1.5 rounded flex items-center gap-2 text-sm font-medium">
                              <FaRegUser
                                size={14}
                                className="text-customGreenColor"
                              />
                              On-site Appointment
                            </span>
                          )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
            {isNextPage && (
              <div className="flex justify-center mt-4">
                <button
                  type="button"
                  onClick={handleLoadMore}
                  disabled={isUpcomingEventsLoading}
                  className={`px-4 py-2 rounded-lg text-white flex items-center gap-2 ${
                    isUpcomingEventsLoading
                      ? "bg-gray-400 cursor-not-allowed"
                      : "bg-customGreenColor"
                  }`}
                >
                  <TbReload
                    className={`${
                      isUpcomingEventsLoading ? "animate-spin" : ""
                    }`}
                  />
                  {isUpcomingEventsLoading ? "Loading..." : "Load More"}
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center min-h-[75vh]">
            <MdSearchOff size={50} className="text-gray-400 pb-1" />
            <p className="text-gray-600">
              No {activeTab === "upcoming" ? "upcoming" : "older"} events found.
            </p>
          </div>
        )}

        {selectedEvent && (
          <Reschedule
            id={selectedEvent.id}
            startTime={selectedEvent.startTime}
            endTime={selectedEvent.endTime}
            calenderEventId={selectedEvent.calenderEventId}
            timeZone={moment.tz.guess()}
            onClose={() => {
              handleCloseEditDialog();
            }}
            onSave={() => {
              setData([]);
              setPageNumber(1);
              handleCloseEditDialog();
              fetchUpcomingEvents();
            }}
          />
        )}
      </div>
    </div>
  );
};

export default Booking;
